import { nanoid } from "nanoid";
import { parse as parseToml } from "smol-toml";
import { normaliseColor } from "./colors";
import { ExpectedError } from "./expectedError";

export type StepApp = { name: string; type: string; note?: string };

export const ROUTE_TYPES = ["webpage", "api", "webhook", "other"] as const;
export type RouteType = (typeof ROUTE_TYPES)[number];

export function isRouteType(v: unknown): v is RouteType {
  return (
    typeof v === "string" && (ROUTE_TYPES as readonly string[]).includes(v)
  );
}

export function isWebpageRoute(v: unknown): boolean {
  return v === "webpage";
}

// Canonical route_auth value for routes exposed to other workflows through a
// workflow-backed connector. `connected_app` is the legacy name still present
// in stored configs; reads accept both via isConnectorRouteAuth until it's
// fully retired.
export const CONNECTOR_ROUTE_AUTH = "connector";

export function isConnectorRouteAuth(routeAuth: string | undefined): boolean {
  return routeAuth === CONNECTOR_ROUTE_AUTH || routeAuth === "connected_app";
}

export type StepConfig = {
  color: string;
  output: boolean;
  route?: string;
  routeAuth?: string;
  routeType?: RouteType;
  externalId?: string;
  emailAddress?: string;
  cron?: string;
  links?: string[];
  apps?: StepApp[];
  timeout?: number;
  retrySeconds?: number[];
  pinned?: boolean;
};

export const MAX_STEP_RETRIES = 10;
export const MAX_STEP_RETRY_SECONDS = 30 * 60;

export function parseStepRetrySeconds(value: unknown): number[] {
  if (value === undefined) return [];
  if (!Array.isArray(value)) {
    throw new ExpectedError("retry_seconds must be an array of seconds.");
  }
  if (value.length > MAX_STEP_RETRIES) {
    throw new ExpectedError(
      `retry_seconds supports at most ${MAX_STEP_RETRIES} retries.`
    );
  }
  if (
    !value.every(
      (seconds) =>
        typeof seconds === "number" &&
        Number.isSafeInteger(seconds) &&
        seconds >= 1 &&
        seconds <= MAX_STEP_RETRY_SECONDS
    )
  ) {
    throw new ExpectedError(
      `retry_seconds values must be whole seconds from 1 to ${MAX_STEP_RETRY_SECONDS}.`
    );
  }
  return [...value] as number[];
}

// Restrictive on purpose — dodges quoting/encoding edge cases in SMTP and keeps addresses memorable.
const EMAIL_LOCALPART_RE = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

export function isValidEmailLocalPart(v: string): boolean {
  return v.length > 0 && v.length <= 64 && EMAIL_LOCALPART_RE.test(v);
}

/**
 * Routes are authenticated by default. A step with a `route` but no
 * `route_auth` is treated as space-scoped. `external_id` auth needs an
 * unguessable id; if the caller didn't supply one, mint it here so the AI
 * can't pick a guessable value.
 */
export function enforceRouteAuth(c: StepConfig): StepConfig {
  if (c.route && !c.routeAuth) {
    return { ...c, routeAuth: "space" };
  }
  if (c.routeAuth === "external_id" && !c.externalId) {
    return { ...c, externalId: nanoid() };
  }
  return c;
}

export function formatStepAppsLine(apps: StepApp[]): string {
  return `apps = [${apps.map((a) => `{ name = ${JSON.stringify(a.name)}, type = ${JSON.stringify(a.type)}${a.note ? `, note = ${JSON.stringify(a.note)}` : ""} }`).join(", ")}]`;
}

export function composeStepConfig(c: StepConfig): string {
  c = enforceRouteAuth(c);
  const lines = [`color = ${JSON.stringify(c.color)}`];
  if (c.output) lines.push("output = true");
  if (c.route) lines.push(`route = ${JSON.stringify(c.route)}`);
  if (c.routeAuth) {
    const value =
      c.routeAuth === "external_id" && c.externalId
        ? `external_id:${c.externalId}`
        : c.routeAuth;
    lines.push(`route_auth = ${JSON.stringify(value)}`);
  }
  if (c.routeType) lines.push(`route_type = ${JSON.stringify(c.routeType)}`);
  if (c.emailAddress)
    lines.push(`email_address = ${JSON.stringify(c.emailAddress)}`);
  if (c.pinned) lines.push("pinned = true");
  if (c.cron) lines.push(`cron = ${JSON.stringify(c.cron)}`);
  if (c.links?.length)
    lines.push(`links = [${c.links.map((l) => JSON.stringify(l)).join(", ")}]`);
  if (c.apps?.length) lines.push(formatStepAppsLine(c.apps));
  if (c.timeout !== undefined) lines.push(`timeout = ${c.timeout}`);
  if (c.retrySeconds?.length) {
    lines.push(`retry_seconds = [${c.retrySeconds.join(", ")}]`);
  }
  return `${lines.join("\n")}\n`;
}

function parseApps(arr: unknown[]): StepApp[] {
  const apps: StepApp[] = [];
  for (const item of arr) {
    if (typeof item === "string") {
      apps.push({ name: item, type: item });
    } else if (item && typeof item === "object") {
      const { name, type, note } = item as Record<string, unknown>;
      if (typeof name === "string" && typeof type === "string") {
        apps.push({ name, type, ...(typeof note === "string" && { note }) });
      }
    }
  }
  return apps;
}

export function scrubAiExternalId(
  afterToml: string,
  beforeToml: string | undefined
): string {
  const after = parseStepConfig(afterToml);
  if (after.routeAuth !== "external_id") return afterToml;
  const before = beforeToml ? parseStepConfig(beforeToml) : null;
  const previousId =
    before?.routeAuth === "external_id" ? before.externalId : undefined;
  if (previousId && after.externalId === previousId) return afterToml;
  return composeStepConfig({ ...after, externalId: previousId });
}

export function parseStepConfig(raw: string): StepConfig {
  let doc: Record<string, unknown>;
  try {
    doc = parseToml(raw);
  } catch (error) {
    throw new ExpectedError(
      `Invalid config.toml: ${error instanceof Error ? error.message : String(error)}`
    );
  }
  const c: StepConfig = { color: "sky", output: false };
  if (typeof doc.color === "string") c.color = normaliseColor(doc.color);
  if (doc.output === true) c.output = true;
  if (typeof doc.route === "string") c.route = doc.route;
  if (typeof doc.route_auth === "string") {
    const colon = doc.route_auth.indexOf(":");
    if (colon !== -1) {
      c.routeAuth = doc.route_auth.slice(0, colon);
      c.externalId = doc.route_auth.slice(colon + 1);
    } else {
      c.routeAuth = doc.route_auth;
    }
    // "oauth" is a retired value: OAuth bearers are a credential the scoped
    // bars accept, not a bar of their own. Blobs are immutable, so stored
    // configs are normalized here rather than rewritten.
    if (c.routeAuth === "oauth") c.routeAuth = "space";
  }
  if (isRouteType(doc.route_type)) c.routeType = doc.route_type;
  if (typeof doc.email_address === "string") {
    const parsed = doc.email_address.toLowerCase();
    if (isValidEmailLocalPart(parsed)) c.emailAddress = parsed;
  }
  if (doc.pinned === true) c.pinned = true;
  if (typeof doc.cron === "string") c.cron = doc.cron;
  if (Array.isArray(doc.links)) {
    const links = doc.links.filter((l): l is string => typeof l === "string");
    const dupe = links.find((l, i) => links.indexOf(l) !== i);
    if (dupe)
      throw new ExpectedError(`Step links to "${dupe}" more than once.`);
    c.links = links;
  }
  if (Array.isArray(doc.apps)) c.apps = parseApps(doc.apps);
  if (typeof doc.timeout === "number") c.timeout = doc.timeout;
  if (doc.retry_seconds !== undefined) {
    c.retrySeconds = parseStepRetrySeconds(doc.retry_seconds);
  }
  return c;
}

/**
 * Step directory for a commit file path, or null for non-step files.
 * Prefer `row.stepDir` when you have a `commit_files` row; this exists
 * for callers that process raw paths pre-row (git push ingestion).
 */
export function stepDirFromPath(path: string): string | null {
  if (path.startsWith(".notes/")) return null;
  const slash = path.indexOf("/");
  return slash === -1 ? null : path.substring(0, slash);
}

/**
 * A path with no step directory is only legitimate at two reserved locations:
 * the workflow-level `README.md` (shown on the Readme tab) and the agent's
 * `.notes/` scratchpad. Every other file must live under a step, since the UI
 * can neither view nor link to a stray file at the workflow root.
 */
export function isAllowedNonStepPath(path: string): boolean {
  return path === "README.md" || path.startsWith(".notes/");
}

export function isScratchStep(dir: string): boolean {
  return dir.startsWith("__scratch_");
}

export const STEP_TESTS_DIR = "tests";

/**
 * A step's test files live under `<stepDir>/tests/`. They travel with the step
 * as commit files but are excluded from the build/run context and content hash,
 * so editing a test never busts the build cache or breaks upstream matching.
 */
export function isStepTestPath(path: string): boolean {
  const dir = stepDirFromPath(path);
  return dir !== null && path.startsWith(`${dir}/${STEP_TESTS_DIR}/`);
}
